import React,{createContext,useContext,useState} from 'react';

const CMSContext=createContext();

export const useCMS=()=> {
  const context=useContext(CMSContext);
  if (!context) {
    throw new Error('useCMS must be used within a CMSProvider');
  }
  return context;
};

export const CMSProvider=({children})=> {
  const [content,setContent]=useState({
    hero: {
      title: "Excellence en QHSE pour votre performance durable",
      subtitle: "Qualité,hygiène,sécurité et environnement... Notre spécialité.",
      description: "Leader en conseil QHSE au Cameroun et dans la sous-région CEMAC. Nous accompagnons les entreprises industrielles,agricoles et commerciales vers la certification ISO et la conformité environnementale.",
      primaryCTA: "Demander un devis",
      secondaryCTA: "Nos services",
    },
    vision: {
      title: "Notre Vision & Valeurs",
      vision: "Être le leader incontournable en conseil QHSE au Cameroun et dans la sous-région CEMAC",
      values: {
        fundamental: {
          title: "Valeurs Fondamentales",
          items: [
            {name: "Innovation",description: "Solutions créatives et technologies avancées"},
            {name: "Intégrité",description: "Transparence et éthique dans toutes nos actions"},
          ],
        },
        professional: {
          title: "Valeurs Professionnelles",
          items: [
            {name: "Performance",description: "Excellence dans l'exécution de nos missions"},
            {name: "Orientation Partenaire",description: "Focus sur le succès de nos clients"},
          ],
        },
      },
    },
    services: {
      title: "Nos Domaines d'Expertise",
      subtitle: "Cinq piliers pour votre excellence QHSE",
      items: [
        {
          id: "assainissement",
          title: "Assainissement",
          description: "Construction de systèmes d'eaux usées,vidange de fosses septiques et installation de biogaz",
          icon: "FiDroplet",
          color: "bg-blue-500",
          details: {
            services: [
              "Construction de systèmes d'évacuation des eaux usées",
              "Vidange et entretien de fosses septiques",
              "Installation de systèmes de biogaz",
              "Audit et diagnostic des installations existantes",
            ],
            benefits: [
              "Conformité environnementale",
              "Réduction de l'impact écologique",
              "Optimisation des coûts",
            ],
          },
        },
        {
          id: "hygiene",
          title: "Hygiène",
          description: "Désinfection agréée Ministère Santé Publique,dératisation et nettoyage industriel",
          icon: "FiShield",
          color: "bg-green-500",
          details: {
            services: [
              "Désinfection agréée (MINSANTE)",
              "Dératisation et contrôle des nuisibles",
              "Nettoyage et décontamination industrielle",
            ],
            benefits: [
              "Environnement sain",
              "Conformité sanitaire",
              "Prévention des risques",
            ],
          },
        },
        {
          id: "controle-pollution",
          title: "Contrôle Pollution",
          description: "Analyses laboratoire eau,aliments,air,bruit et poussières",
          icon: "FiActivity",
          color: "bg-yellow-500",
          details: {
            services: [
              "Analyse qualité de l'eau",
              "Mesure des polluants atmosphériques (CO,NOx,H2S)",
              "Analyse des poussières et bruit",
            ],
            benefits: [
              "Protection de la santé",
              "Surveillance environnementale",
              "Respect des normes",
            ],
          },
        },
        {
          id: "formation",
          title: "Formation & Séminaires",
          description: "Modules Management QHSE,Secourisme,Lutte incendie",
          icon: "FiBook",
          color: "bg-purple-500",
          details: {
            services: [
              "Management QHSE",
              "Secourisme et premiers secours",
              "Lutte contre l'incendie",
            ],
            benefits: [
              "Personnel certifié",
              "Culture sécurité",
              "Réduction des accidents",
            ],
          },
        },
        {
          id: "support-qhse",
          title: "Support QHSE",
          description: "Accompagnement certification ISO 9001,14001,22000 et 45001",
          icon: "FiAward",
          color: "bg-red-500",
          details: {
            services: [
              "Certification ISO 9001 & 14001",
              "Support ISO 45001 & 22000",
              "Audit et amélioration continue",
            ],
            benefits: [
              "Reconnaissance internationale",
              "Efficacité opérationnelle",
              "Confiance client",
            ],
          },
        },
      ],
    },
    company: {
      name: "QHSE Consulting",
      founded: "2016-09",
      mission: "Améliorer la performance de nos partenaires à travers l'excellence en Qualité,Hygiène,Sécurité et Environnement",
      address: "Cité des Palmiers,Douala,Cameroun",
      phone: "+237 675 78 46 56",
      phone2: "+237 658 97 00 98",
      email: "contact@qhseconsulting.biz",
      taxpayer: "M051916001234A",
      rccm: "RC/DLA/2016/B/1234",
      logo: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1768133277599-Logo%20qhse%20consulting%20GF.png",
    },
    testimonials: {
      title: "Témoignages",
      items: [
        {
          name: "Jean-Paul Mbarga",
          company: "Agro-Industries SA",
          text: "Un accompagnement exceptionnel pour notre passage à l'ISO 14001.",
        },
        {
          name: "Marie Dupont",
          company: "Industries Alimentaires",
          text: "Service professionnel et de qualité. Très satisfait des résultats.",
        },
      ],
    },
    trust: {
      title: "Nos Agréments",
      approvals: ["Ministère de la Santé Publique","Ministère de l'Industrie et des Mines"],
    },
  });

  const [leads,setLeads]=useState([]);

  const updateContent=(section,data)=> {
    setContent((prev)=> ({
      ...prev,
      [section]: {...prev[section],...data},
    }));
  };

  const addLead=(leadData)=> {
    setLeads((prev)=> [
      {
        id: Date.now(),
        ...leadData,
        createdAt: new Date().toISOString(),
      },
      ...prev,
    ]);
  };

  const addTestimonial=(testimonialData)=> {
    setContent((prev)=> ({
      ...prev,
      testimonials: {
        ...prev.testimonials,
        items: [
          {
            id: Date.now(),
            ...testimonialData,
          },
          ...prev.testimonials.items,
        ],
      },
    }));
  };

  const updateTestimonial=(testimonialId,testimonialData)=> {
    setContent((prev)=> ({
      ...prev,
      testimonials: {
        ...prev.testimonials,
        items: prev.testimonials.items.map((item)=>
          item.id===testimonialId ? {...item,...testimonialData} : item
        ),
      },
    }));
  };

  const deleteTestimonial=(testimonialId)=> {
    setContent((prev)=> ({
      ...prev,
      testimonials: {
        ...prev.testimonials,
        items: prev.testimonials.items.filter((item)=> item.id !==testimonialId),
      },
    }));
  };

  return (
    <CMSContext.Provider
      value={{
        content,
        updateContent,
        leads,
        addLead,
        addTestimonial,
        updateTestimonial,
        deleteTestimonial,
      }}
    >
      {children}
    </CMSContext.Provider>
  );
};